<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import { useFetch } from "@/plugins/fetch";
import { useToast } from 'primevue/usetoast';
import { useSensorStore } from '@/store/SensorStore';

// PrimeVue and other components
import Sidebar from 'primevue/sidebar';
import Calendar from 'primevue/calendar';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import Skeleton from 'primevue/skeleton';
import Chart from 'primevue/chart';
import ArgonButton from "@/components/Argon/ArgonButton.vue";

// HOOKS
const fetch = useFetch();
const toast = useToast();
const sensorStore = useSensorStore();

// STATE
const isSidebarVisible = ref(false);
const isLoading = ref(false);

const form = ref({
  sensorIds: [],
  dataTypes: [],
  startDate: null,
  endDate: null,
});

const chartData = reactive({
  labels: [],
  datasets: []
});

const chartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: '#495057'
      }
    }
  },
  scales: {
    x: { ticks: { color: '#495057' } },
    y: { ticks: { color: '#495057' } }
  }
});

const dataTypeOptions = ref([
  { label: 'Temperature', value: 'temperature' },
  { label: 'Humidity', value: 'humidity' },
  { label: 'Pressure', value: 'pressure' },
]);

// COMPUTED
const sensorOptions = computed(() => sensorStore.sensors.map(s => ({ name: s.name, id: s.unique_id })));
const isFetchDisabled = computed(() => !form.value.sensorIds.length || !form.value.dataTypes.length || !form.value.startDate || !form.value.endDate);

// LIFECYCLE
onMounted(() => {
  sensorStore.fetchSensors();
});

// METHODS
const fetchDataAndProcessChart = async () => {
  if (isFetchDisabled.value) return;
  isLoading.value = true;
  isSidebarVisible.value = false;
  try {
    const params = new URLSearchParams({
      sensorIds: form.value.sensorIds.join(','),
      startDate: form.value.startDate.toISOString(),
      endDate: form.value.endDate.toISOString(),
      dataTypes: form.value.dataTypes.join(','),
    });
    const response = await fetch(`sensors/data?${params}`);

    if (!response.length) {
      toast.add({ severity: 'info', summary: 'No Data', detail: 'No data found for the selected criteria.', life: 4000 });
      chartData.labels = [];
      chartData.datasets = [];
      return;
    }

    processChartData(response);

  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch chart data.', life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

const processChartData = (data) => {
  const datasetsMap = new Map();
  const labels = [...new Set(data.map(item => new Date(item.time).toLocaleString()))].sort();

  const colors = ['#42A5F5', '#66BB6A', '#FFA726', '#26A69A', '#AB47BC', '#EC407A'];
  let colorIndex = 0;

  data.forEach(item => {
    const sensorName = sensorStore.sensors.find(s => s.unique_id === item.sensor_id)?.name || item.sensor_id;
    const datasetKey = `${sensorName} - ${item.data_type}`;

    if (!datasetsMap.has(datasetKey)) {
      datasetsMap.set(datasetKey, {
        label: datasetKey,
        data: new Array(labels.length).fill(null),
        borderColor: colors[colorIndex % colors.length],
        tension: 0.4,
        fill: false
      });
      colorIndex++;
    }

    const index = labels.indexOf(new Date(item.time).toLocaleString());
    if (index !== -1) {
      datasetsMap.get(datasetKey).data[index] = item.value;
    }
  });

  chartData.labels = labels;
  chartData.datasets = Array.from(datasetsMap.values());
};

</script>

<template>
  <div class="py-4 container-fluid">
    <Toast />
    <div class="card">
      <div class="card-header border-bottom">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h6 class="mb-0">Data Visualisation</h6>
            <p class="text-sm mb-0">Create custom graphs from historical sensor data.</p>
          </div>
          <ArgonButton color="primary" variant="gradient" @click="isSidebarVisible = true">
            <i class="fas fa-chart-line me-2"></i>
            Select Data & Build Graph
          </ArgonButton>
        </div>
      </div>

      <div class="card-body">
        <div v-if="isLoading" class="p-4">
          <Skeleton height="25rem" />
        </div>
        <div v-else-if="chartData.datasets.length" style="height: 60vh">
          <Chart type="line" :data="chartData" :options="chartOptions" />
        </div>
        <div v-else class="text-center py-5">
          <i class="fas fa-drafting-compass fa-3x text-muted mb-3"></i>
          <h5 class="text-muted">Visualise Your Data</h5>
          <p>Use the panel to select sensors and build a custom graph.</p>
        </div>
      </div>
    </div>

    <Sidebar v-model:visible="isSidebarVisible" position="right" class="p-sidebar-md">
      <template #header>
        <h5 class="m-0">Graph Builder</h5>
      </template>
      <div class="p-fluid">
        <div class="mb-4">
          <label class="form-label fw-bold">1. Select Sensors</label>
          <MultiSelect v-model="form.sensorIds" :options="sensorOptions" optionLabel="name" optionValue="id" placeholder="Choose one or more sensors" display="chip" />
        </div>
        <div class="mb-4">
          <label class="form-label fw-bold">2. Select Data Types</label>
          <MultiSelect v-model="form.dataTypes" :options="dataTypeOptions" optionLabel="label" optionValue="value" placeholder="Choose data types" display="chip" />
        </div>
        <div class="mb-4">
          <label class="form-label fw-bold">3. Select Date Range</label>
          <Calendar v-model="form.startDate" placeholder="Start Date" :showTime="true" lang="en-GB" class="mb-2 w-100" date-format="DD/MM/YYYY" />
          <Calendar v-model="form.endDate" placeholder="End Date" :showTime="true" class="w-100" />
        </div>
      </div>
      <template #footer>
        <div class="d-grid">
          <Button label="Generate Graph" icon="pi pi-chart-line" @click="fetchDataAndProcessChart" :disabled="isFetchDisabled" />
        </div>
      </template>
    </Sidebar>
  </div>
</template>
<style>
.p-sidebar-md {
  width: 25rem;
}
</style>
